<template>
  <v-card
    id="minimap-drag-content"
    ref="minimapcontainer"
    elevation="6"
    class="minimap-container mr-12 mb-15"
  >
    <div ref="minimap" class="minimap" />
    <v-toolbar :height="collapsedHeight" class="minimap-toolbar">
      <v-icon id="drag-icon" ref="dragicon" color="on-surface">
        {{ CONSTANTS.ICONS_MDI.ICON_DRAG }}
      </v-icon>
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn
            color="on-surface"
            v-bind="props"
            :icon="CONSTANTS.ICONS_MDI.ICON_MAGNIFY_PLUS"
            @click="zoomIn()"
          >
          </v-btn>
        </template>
        <span>{{ labels.EXPLORER.MINIMAP.ZOOM_IN }}</span>
      </v-tooltip>

      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn
            :icon="CONSTANTS.ICONS_MDI.ICON_ARROW_EXPAND_ALL"
            color="on-surface"
            v-bind="props"
            @click="zoomToFit()"
          >
          </v-btn>
        </template>
        <span>{{ labels.EXPLORER.MINIMAP.ZOOM_TO_FIT }}</span>
      </v-tooltip>
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn
            :icon="CONSTANTS.ICONS_MDI.ICON_ARROW_COLLAPSE_ALL"
            color="on-surface"
            v-bind="props"
            @click="zoomToOriginal()"
          >
          </v-btn>
        </template>
        <span>{{ labels.EXPLORER.MINIMAP.ZOOM_TO_ORIGINAL }}</span>
      </v-tooltip>

      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn
            :icon="CONSTANTS.ICONS_MDI.ICON_MAGNIFY_MINUS"
            color="on-surface"
            v-bind="props"
            @click="zoomOut()"
          >
          </v-btn>
        </template>
        <span>{{ labels.EXPLORER.MINIMAP.ZOOM_OUT }}</span>
      </v-tooltip>

      <v-text-field
        v-model="zoomFactor"
        hide-details
        single-line
        class="zoom-field-style"
        variant="filled"
        :rules="[
          (value: any) => checkBottomRangeValidity(value),
          (value: any) => checkUpperRangeValidity(value),
        ]"
        validate-on="submit"
        :min="MIN"
        :max="MAX"
        density="compact"
        type="number"
        suffix="%"
        @keydown.enter="updateZoom()"
        @blur="updateZoom()"
        @click="updateZoom()"
      />
      <v-spacer />

      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn
            icon
            :color="minimized ? 'on-surface' : 'cyan-dark'"
            v-bind="props"
            @click="minMax()"
          >
            <v-icon>{{ CONSTANTS.ICONS_MDI.ICON_MAP }}</v-icon>
          </v-btn>
        </template>
        <span>{{ labels.EXPLORER.MINIMAP.SHOW_HIDE }}</span>
      </v-tooltip>
    </v-toolbar>
  </v-card>
</template>

<script lang="ts" setup>
  import CONSTANTS from '@/constants/Config.constants'
  import labels from '@/translations/labels.json'
  import { ui } from '@clientio/rappid'
  import DragHandler from '@/services/impl/DragHandler'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useDisplay } from 'vuetify'
  import { useEventBus } from '@/utils/events'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'

  const collapsedHeight = ref<number>(35)

  const { lgAndDown } = useDisplay()

  const rappidContext = injectTyped(ServiceKeys.rappidContext)

  const ZOOM_STEP = 0.05
  const MAX = 150
  const MIN = 5

  const nav = ref<ui.Navigator>()
  const zoomOptions = ref<ui.PaperScroller.ZoomOptions>({ max: 1.5, min: ZOOM_STEP })
  const zoomStep = ZOOM_STEP
  const zoomFactor = ref<number>(100)
  const minimized = ref(!lgAndDown.value)
  let dragHandler: DragHandler

  watch(
    () => lgAndDown.value,
    (mdMode: boolean) => {
      minimized.value = !mdMode
      minMax()
    },
  )

  const dragIconTarget = computed(() => {
    return document.getElementById('drag-icon') as HTMLElement
  })

  const dragMinimapContent = computed(() => {
    return document.getElementById('minimap-drag-content') as HTMLElement
  })

  useEventBus('toggleExplorerSidebar', (state) => {
    if (dragHandler) {
      if (state) dragHandler.setRestriction(330)
      else dragHandler.setRestriction(720)
    }
  })

  onMounted(() => {
    nav.value = new ui.Navigator({
      paperScroller: rappidContext.paperScroller,
      width: 370,
      padding: 8,
      zoomOptions: zoomOptions.value,
      paperOptions: {
        routerNamespace: rappidContext.paper.options.routerNamespace,
      },
    })

    nav.value.$el.appendTo('.minimap')
    nav.value.render()
    rappidContext.paperScroller.on('scroll', () => updateZoomFactor())
    minMax()

    dragHandler = new DragHandler(dragIconTarget.value, dragMinimapContent.value, 'explorer')
    dragHandler.setRestriction(720)
    dragHandler.enableDragging()
  })

  function checkBottomRangeValidity(value: string): boolean {
    return parseInt(value) >= (zoomOptions.value.min ?? 0) * 100
  }

  function checkUpperRangeValidity(value: string): boolean {
    return parseInt(value) <= (zoomOptions.value.max ?? 1) * 100
  }

  function updateZoomFactor(): void {
    zoomFactor.value = Math.round(rappidContext.paperScroller.zoom() * 100)
  }

  function updateZoom(): void {
    const updateStep =
      (zoomFactor.value - Math.round(rappidContext.paperScroller.zoom() * 100)) / 100
    rappidContext.paperScroller.zoom(updateStep, zoomOptions.value)
  }

  function zoomIn(): void {
    rappidContext.paperScroller.zoom(zoomStep, zoomOptions.value)
  }

  function zoomOut(): void {
    rappidContext.paperScroller.zoom(-zoomStep, zoomOptions.value)
  }

  function zoomToFit(): void {
    rappidContext.paperScroller.zoomToFit({
      padding: { left: 20, top: 20, right: 20, bottom: 20 },
      maxScale: 1,
    })
  }

  function zoomToOriginal(): void {
    rappidContext.paperScroller.zoom(1 - rappidContext.paperScroller.zoom(), zoomOptions.value)
    rappidContext.paperScroller.centerContent()
  }

  function minMax(): void {
    if (minimized.value) maximize()
    else minimize()
  }

  function minimize(): void {
    minimized.value = true
    dragMinimapContent.value.style.height = `${collapsedHeight.value}px`
    nav.value?.freeze()
  }

  function maximize(): void {
    minimized.value = false
    dragMinimapContent.value.style.height = '248px'
    nav.value?.unfreeze()
    nav.value?.updateCurrentView()
  }
</script>
<style lang="scss" scoped>
  .zoom-field-style {
    width: 90px;
  }
  .minimap-container {
    position: absolute;
    bottom: 20px;
    left: 20px;
    width: 370px;
    height: 248px;
    padding: 0px;
    z-index: 1;
  }
  .minimap {
    position: absolute;
    top: 0px;
    left: 0px;
  }
  .minimap-toolbar {
    position: absolute;
    bottom: 0px;
    width: 370px;
  }

  /* Media queries */
  @media (max-height: 695px) {
    .minimap-container {
      left: 0;
      right: 0;
      margin: auto;
    }
  }
</style>
